import { useEffect, useRef } from 'preact/hooks';
import type { Ref, RefObject } from 'preact';
import { Signal } from '@preact/signals';

import {
  trackVideoComplete,
  trackVideoPause,
  trackVideoPlay,
  trackVideoProgress,
  type LastEventSource
} from '@/widgets/ProductDetailsHero/Tracking.ts';

type MilestonesRef = {
  75: boolean;
  50: boolean;
  25: boolean;
  10: boolean;
};

const milestones: (keyof MilestonesRef)[] = [10, 25, 50, 75];

function trackVideoProgressOnce(
  videoId: string,
  ctaName: LastEventSource,
  progressPercentage: number,
  milestonesRef: React.RefObject<MilestonesRef>,
  loop: number
) {
  if (!milestonesRef?.current) {
    return;
  }

  for (const threshold of milestones) {
    if (progressPercentage >= threshold && !milestonesRef.current[threshold]) {
      // eslint-disable-next-line no-param-reassign
      milestonesRef.current[threshold] = true;
      trackVideoProgress(videoId, ctaName, loop, threshold);
    }
  }
}

// these functions allow the types to work when passing refs around
export function assignRef<T = any>(ref: Ref<T>, value: T | null) {
  if (typeof ref === 'function') {
    ref(value);
  } else if (ref && typeof ref === 'object') {
    // eslint-disable-next-line no-param-reassign
    (ref as RefObject<T>).current = value;
  }
}

export function useSyncedRef<T>(ref?: Ref<T>): RefObject<T> {
  const innerRef = useRef<T>(null);

  useEffect(() => {
    if (ref) {
      assignRef<T>(ref, innerRef.current);
    }
  }, [ref]);

  return innerRef;
}

export const lastEventSource = new Signal<LastEventSource>('default image');
const loop = new Signal<number>(0);
const eventsAdded = new Signal<boolean>(false);

export default function useVideoTracking(
  videoUrl: string,
  ref?: Ref<HTMLVideoElement>
) {
  const milestonesRef = useRef<MilestonesRef>({
    10: false,
    25: false,
    50: false,
    75: false
  });

  const videoRef = useSyncedRef(ref);

  // Reset milestones when the video ends, we always call 100% progress tracking on end to make sure there is no race condition.
  const handleEnded = () => {
    milestonesRef.current = { 10: false, 25: false, 50: false, 75: false };
    trackVideoComplete(videoUrl, lastEventSource.value);
    loop.value += 1;
  };

  const handleVideoProgress = () => {
    const currentTime = videoRef?.current?.currentTime;
    const duration = videoRef?.current?.duration;

    if (duration && currentTime) {
      const progressPercentage = (currentTime / duration) * 100;

      trackVideoProgressOnce(
        videoUrl,
        lastEventSource.value,
        progressPercentage,
        milestonesRef,
        loop.value
      );
    }
  };

  useEffect(() => {
    if (!eventsAdded.value && videoRef?.current) {
      videoRef?.current?.addEventListener('timeupdate', handleVideoProgress);
      videoRef?.current?.addEventListener('ended', handleEnded);
      eventsAdded.value = true;
    }

    return () => {
      videoRef?.current?.removeEventListener('timeupdate', handleVideoProgress);
      videoRef?.current?.removeEventListener('ended', handleEnded);
      eventsAdded.value = false;
    };
  }, [videoRef?.current]);

  return {
    lastEventSource,
    trackVideoPlay: () => {
      trackVideoPlay(videoUrl, lastEventSource.value);
    },
    trackVideoPause: () => {
      trackVideoPause(videoUrl, lastEventSource.value);
    },
    videoRef
  };
}
